// Pure builders that turn a barcode SVG + a page layout spec into a printable
// page SVG (in millimetre units). Used by both the popup-print path and the
// PDF download path so the output is identical.

export type PaperSize = "A4" | "letter";
export type PaperOrientation = "portrait" | "landscape";

export type SingleLayout = {
  kind: "single";
  /** Target barcode width in millimetres. Height is derived from the source aspect ratio. */
  widthMm: number;
};

export type GridLayout = {
  kind: "grid";
  /** Number of columns of labels. */
  cols: number;
  /** Number of rows of labels. */
  rows: number;
  /** Target cell barcode width in millimetres. */
  widthMm: number;
};

/** A label-sheet preset (Avery 5160, L7160, ...) defines fixed paper, grid
 *  pitch and label dimensions. The barcode auto-sizes to the label.
 *  `copies` lets the user fill only the first N labels when they don't need
 *  a full sheet.
 */
export type SheetLayout = {
  kind: "sheet";
  preset: SheetPresetId;
  /** Number of labels to print, 1..preset.cells. Remaining labels are blank. */
  copies: number;
};

/** Data that gets drawn alongside the QR on a Wi-Fi credit-card. */
export type WifiCardData = {
  ssid: string;
  password: string;
  security: "WPA" | "WPA2" | "WPA3" | "WEP" | "nopass";
  hidden: boolean;
};

/** Data that gets drawn alongside the QR on a vCard credit-card. */
export type VcardCardData = {
  firstName: string;
  lastName: string;
  title: string;
  organization: string;
  phone: string;
  email: string;
  website: string;
};

export type CardSheetId = "single" | "averyL7414" | "avery5371";

/** Business-card sized template (CR80 ~ Avery L7414 / 5371). The QR sits on
 *  the left, readable contact / Wi-Fi fields on the right. */
export type CardLayout =
  | { kind: "wifi-card"; sheet: CardSheetId; copies: number; data: WifiCardData }
  | { kind: "vcard-card"; sheet: CardSheetId; copies: number; data: VcardCardData };

export type PageLayout = SingleLayout | GridLayout | SheetLayout | CardLayout;

export type PrintLayoutOptions = {
  /** Source barcode SVG (full <svg>...</svg> markup, e.g. from bwip-js.toSVG). */
  svgMarkup: string;
  /** Caption printed under each barcode. Empty string suppresses the caption. */
  caption: string;
  /** Ignored when layout.kind === "sheet" (the preset fixes the paper). */
  paper: PaperSize;
  /** Ignored when layout.kind === "sheet" (the preset fixes the orientation). */
  orientation: PaperOrientation;
  layout: PageLayout;
  /** Page margin in millimetres on every side. Ignored for sheet presets. */
  marginMm?: number;
};

const PAPER_MM: Record<PaperSize, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  letter: { w: 215.9, h: 279.4 },
};

// ---------- Label-sheet presets ----------

export type SheetPresetId = "avery5160" | "avery5163" | "averyL7160" | "averyL7163";

export type SheetPreset = {
  id: SheetPresetId;
  /** Human-readable label shown in the dialog. */
  label: string;
  paper: PaperSize;
  orientation: PaperOrientation;
  cols: number;
  rows: number;
  /** Distance from page edge to first label's top-left corner, in mm. */
  marginLeftMm: number;
  marginTopMm: number;
  /** Label outer dimensions, in mm. */
  labelWMm: number;
  labelHMm: number;
  /** Centre-to-centre pitch between adjacent labels, in mm. */
  pitchXMm: number;
  pitchYMm: number;
};

/** Standard adhesive label sheets. Dimensions taken from Avery / Avery UK
 *  spec sheets — all values in millimetres. */
export const SHEET_PRESETS: Record<SheetPresetId, SheetPreset> = {
  avery5160: {
    id: "avery5160",
    label: "Avery 5160 — 30-up address (US Letter)",
    paper: "letter",
    orientation: "portrait",
    cols: 3,
    rows: 10,
    marginLeftMm: 4.7625, // 0.1875"
    marginTopMm: 12.7, // 0.5"
    labelWMm: 66.675, // 2.625"
    labelHMm: 25.4, // 1"
    pitchXMm: 69.85, // 2.75"
    pitchYMm: 25.4, // 1"
  },
  avery5163: {
    id: "avery5163",
    label: "Avery 5163 — 10-up shipping (US Letter)",
    paper: "letter",
    orientation: "portrait",
    cols: 2,
    rows: 5,
    marginLeftMm: 4.064, // 0.16"
    marginTopMm: 12.7, // 0.5"
    labelWMm: 101.6, // 4"
    labelHMm: 50.8, // 2"
    pitchXMm: 106.426, // 4.19"
    pitchYMm: 50.8, // 2"
  },
  averyL7160: {
    id: "averyL7160",
    label: "Avery L7160 — 21-up address (A4)",
    paper: "A4",
    orientation: "portrait",
    cols: 3,
    rows: 7,
    marginLeftMm: 7,
    marginTopMm: 15.1,
    labelWMm: 63.5,
    labelHMm: 38.1,
    pitchXMm: 66,
    pitchYMm: 38.1,
  },
  averyL7163: {
    id: "averyL7163",
    label: "Avery L7163 — 14-up shipping (A4)",
    paper: "A4",
    orientation: "portrait",
    cols: 2,
    rows: 7,
    marginLeftMm: 4.65,
    marginTopMm: 15.1,
    labelWMm: 99.1,
    labelHMm: 38.1,
    pitchXMm: 101.6,
    pitchYMm: 38.1,
  },
};

export function sheetCellCount(preset: SheetPreset): number {
  return preset.cols * preset.rows;
}

// ---------- Business-card sheet specs ----------

type CardSheetSpec = {
  id: CardSheetId;
  label: string;
  paper: PaperSize;
  orientation: PaperOrientation;
  /** A single big card centred on the page, or a tiled card sheet. */
  cols: number;
  rows: number;
  cardWMm: number;
  cardHMm: number;
  marginLeftMm: number;
  marginTopMm: number;
  pitchXMm: number;
  pitchYMm: number;
};

/** CR80 (85.6 × 53.98 mm) on its own A4 page, or one of the two common
 *  10-up business-card sheets. */
export const CARD_SHEETS: Record<CardSheetId, CardSheetSpec> = {
  single: {
    id: "single",
    label: "Single card (centred)",
    paper: "A4",
    orientation: "portrait",
    cols: 1,
    rows: 1,
    cardWMm: 85.6,
    cardHMm: 53.98,
    // Position is computed at render time to centre the card.
    marginLeftMm: (210 - 85.6) / 2,
    marginTopMm: (297 - 53.98) / 2,
    pitchXMm: 85.6,
    pitchYMm: 53.98,
  },
  averyL7414: {
    id: "averyL7414",
    label: "Avery L7414 — 10-up business cards (A4)",
    paper: "A4",
    orientation: "portrait",
    cols: 2,
    rows: 5,
    cardWMm: 85,
    cardHMm: 54,
    marginLeftMm: 20, // (210 − 2×85) / 2 — cards abut with no horizontal gutter
    marginTopMm: 13.5,
    pitchXMm: 85, // gapless: pitch equals card width so the block stays centred
    pitchYMm: 54,
  },
  avery5371: {
    id: "avery5371",
    label: "Avery 5371 — 10-up business cards (US Letter)",
    paper: "letter",
    orientation: "portrait",
    cols: 2,
    rows: 5,
    cardWMm: 88.9, // 3.5"
    cardHMm: 50.8, // 2"
    marginLeftMm: 19.05, // 0.75"
    marginTopMm: 12.7, // 0.5"
    pitchXMm: 88.9, // gapless 3.5" cards — pitch equals card width, block stays centred
    pitchYMm: 50.8,
  },
};

export function cardSheetCellCount(spec: CardSheetSpec): number {
  return spec.cols * spec.rows;
}

/** Strip the XML declaration and outer <svg ...> wrapper to leave the inner
 *  content. We need this because we want to re-wrap the barcode in our own
 *  outer <svg> with explicit width / height. Returns inner markup + viewBox. */
function unwrapSvg(svg: string): { inner: string; vbW: number; vbH: number } {
  const noXml = svg.replace(/^<\?xml[^?]*\?>\s*/, "");
  const open = noXml.match(/<svg[^>]*>/);
  const close = noXml.lastIndexOf("</svg>");
  if (!open || close < 0) return { inner: noXml, vbW: 100, vbH: 100 };
  const opening = open[0];
  const vb = opening.match(/viewBox="([\d.\-\s]+)"/);
  let vbW = 100;
  let vbH = 100;
  if (vb) {
    const parts = vb[1].split(/\s+/).map(Number);
    if (parts.length === 4 && Number.isFinite(parts[2]) && Number.isFinite(parts[3])) {
      vbW = parts[2];
      vbH = parts[3];
    }
  } else {
    const w = opening.match(/\bwidth="([\d.]+)"/);
    const h = opening.match(/\bheight="([\d.]+)"/);
    if (w) vbW = Number(w[1]);
    if (h) vbH = Number(h[1]);
  }
  const inner = noXml.slice(opening.length, close);
  return { inner, vbW, vbH };
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Build a printable A4 / Letter SVG with one or more barcodes laid out per
 *  the given layout. The result has explicit mm units so browser print and
 *  PDF embedding produce a sheet at exact physical size. */
export function buildPageSvg(opts: PrintLayoutOptions): string {
  if (opts.layout.kind === "wifi-card" || opts.layout.kind === "vcard-card") {
    return buildCardPageSvg(opts.svgMarkup, opts.layout);
  }
  const sheet = opts.layout.kind === "sheet" ? SHEET_PRESETS[opts.layout.preset] : null;
  const effectivePaper: PaperSize = sheet ? sheet.paper : opts.paper;
  const effectiveOrient: PaperOrientation = sheet ? sheet.orientation : opts.orientation;
  const paper = PAPER_MM[effectivePaper];
  const margin = opts.marginMm ?? 10;
  const pageW = effectiveOrient === "portrait" ? paper.w : paper.h;
  const pageH = effectiveOrient === "portrait" ? paper.h : paper.w;
  const { inner, vbW, vbH } = unwrapSvg(opts.svgMarkup);
  const aspect = vbH / vbW;

  // Caption typography: ~10pt at 0.353 mm/pt = 3.53 mm; line-height ~4 mm.
  const captionMm = opts.caption ? 4 : 0;

  const cells: { x: number; y: number; w: number; h: number }[] = [];

  if (opts.layout.kind === "sheet" && sheet) {
    // Pad inside each label so the barcode doesn't touch the cutting edge.
    const padMm = 1.5;
    const innerW = sheet.labelWMm - padMm * 2;
    const innerH = sheet.labelHMm - padMm * 2 - captionMm;
    // Fit while preserving aspect ratio: pick the smaller of width / height.
    const widthBoundW = innerW;
    const widthBoundH = innerW * aspect;
    let w: number;
    let h: number;
    if (widthBoundH <= innerH) {
      w = widthBoundW;
      h = widthBoundH;
    } else {
      h = innerH;
      w = innerH / aspect;
    }
    const cap = clamp(opts.layout.copies, 1, sheetCellCount(sheet));
    let placed = 0;
    for (let r = 0; r < sheet.rows && placed < cap; r++) {
      for (let c = 0; c < sheet.cols && placed < cap; c++) {
        const cellX = sheet.marginLeftMm + c * sheet.pitchXMm;
        const cellY = sheet.marginTopMm + r * sheet.pitchYMm;
        cells.push({
          x: cellX + (sheet.labelWMm - w) / 2,
          y: cellY + (sheet.labelHMm - (h + captionMm)) / 2,
          w,
          h,
        });
        placed++;
      }
    }
  } else if (opts.layout.kind === "single") {
    const w = clamp(opts.layout.widthMm, 5, pageW - margin * 2);
    const h = w * aspect;
    const totalH = h + captionMm;
    cells.push({
      x: (pageW - w) / 2,
      y: Math.max(margin, (pageH - totalH) / 2),
      w,
      h,
    });
  } else if (opts.layout.kind === "grid") {
    const cols = clamp(opts.layout.cols, 1, 20);
    const rows = clamp(opts.layout.rows, 1, 50);
    const usableW = pageW - margin * 2;
    const usableH = pageH - margin * 2;
    const cellOuterW = usableW / cols;
    const cellOuterH = usableH / rows;
    const w = clamp(opts.layout.widthMm, 5, cellOuterW - 4);
    const h = w * aspect;
    const totalH = h + captionMm;
    if (totalH > cellOuterH - 4) {
      // shrink to fit cell height too
      const maxH = cellOuterH - 4 - captionMm;
      const shrunkW = maxH / aspect;
      const shrunkH = maxH;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          cells.push({
            x: margin + c * cellOuterW + (cellOuterW - shrunkW) / 2,
            y: margin + r * cellOuterH + (cellOuterH - (shrunkH + captionMm)) / 2,
            w: shrunkW,
            h: shrunkH,
          });
        }
      }
    } else {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          cells.push({
            x: margin + c * cellOuterW + (cellOuterW - w) / 2,
            y: margin + r * cellOuterH + (cellOuterH - (h + captionMm)) / 2,
            w,
            h,
          });
        }
      }
    }
  }

  const parts: string[] = [];
  parts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${pageW}mm" height="${pageH}mm" viewBox="0 0 ${pageW} ${pageH}">`,
  );
  parts.push(`<rect width="${pageW}" height="${pageH}" fill="#ffffff" />`);

  const captionTxt = opts.caption ? escapeXml(opts.caption) : "";
  for (const cell of cells) {
    parts.push(
      `<svg x="${cell.x.toFixed(3)}" y="${cell.y.toFixed(3)}" width="${cell.w.toFixed(3)}" height="${cell.h.toFixed(3)}" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet">${inner}</svg>`,
    );
    if (captionTxt) {
      parts.push(
        `<text x="${(cell.x + cell.w / 2).toFixed(3)}" y="${(cell.y + cell.h + 3.2).toFixed(3)}" text-anchor="middle" font-family="ui-monospace, Menlo, Consolas, monospace" font-size="2.6" fill="#111111">${captionTxt}</text>`,
      );
    }
  }

  parts.push(`</svg>`);
  return parts.join("\n");
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Width / height of a layout's paper in mm, post-orientation. */
export function getPaperMm(
  paper: PaperSize,
  orientation: PaperOrientation,
): { w: number; h: number } {
  const p = PAPER_MM[paper];
  return orientation === "portrait" ? { w: p.w, h: p.h } : { w: p.h, h: p.w };
}

// ---------- Card templates ----------

const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const MONO_STACK = "ui-monospace, Menlo, Consolas, monospace";

function buildCardPageSvg(svgMarkup: string, layout: CardLayout): string {
  const spec = CARD_SHEETS[layout.sheet];
  const paper = PAPER_MM[spec.paper];
  const pageW = spec.orientation === "portrait" ? paper.w : paper.h;
  const pageH = spec.orientation === "portrait" ? paper.h : paper.w;
  const { inner: qrInner, vbW, vbH } = unwrapSvg(svgMarkup);

  const parts: string[] = [];
  parts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${pageW}mm" height="${pageH}mm" viewBox="0 0 ${pageW} ${pageH}">`,
  );
  parts.push(`<rect width="${pageW}" height="${pageH}" fill="#ffffff" />`);

  const total = cardSheetCellCount(spec);
  const copies = clamp(layout.copies, 1, total);
  let placed = 0;
  for (let r = 0; r < spec.rows && placed < copies; r++) {
    for (let c = 0; c < spec.cols && placed < copies; c++) {
      const x = spec.marginLeftMm + c * spec.pitchXMm;
      const y = spec.marginTopMm + r * spec.pitchYMm;
      parts.push(renderCard(layout, x, y, spec.cardWMm, spec.cardHMm, qrInner, vbW, vbH));
      placed++;
    }
  }

  parts.push(`</svg>`);
  return parts.join("\n");
}

function renderCard(
  layout: CardLayout,
  x: number,
  y: number,
  w: number,
  h: number,
  qrInner: string,
  vbW: number,
  vbH: number,
): string {
  const padMm = 3;
  const qrSize = h - padMm * 2;
  const qrX = x + padMm;
  const qrY = y + padMm;
  const textX = x + qrSize + padMm * 2;
  const textTopY = y + padMm;
  const textW = w - qrSize - padMm * 3;

  const bg = `<rect x="${x.toFixed(3)}" y="${y.toFixed(3)}" width="${w.toFixed(3)}" height="${h.toFixed(3)}" rx="3" ry="3" fill="#ffffff" stroke="#cbd5e1" stroke-width="0.25" />`;
  const qr = `<svg x="${qrX.toFixed(3)}" y="${qrY.toFixed(3)}" width="${qrSize.toFixed(3)}" height="${qrSize.toFixed(3)}" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet">${qrInner}</svg>`;

  let content = "";
  if (layout.kind === "wifi-card") {
    content = renderWifiCardContent(layout.data, textX, textTopY, textW);
  } else {
    content = renderVcardCardContent(layout.data, textX, textTopY, textW);
  }
  return bg + qr + content;
}

/** Trim a string so it fits within an estimated mm width at a given font
 *  size. The estimate uses 0.55 ✕ font-size as average character width, which
 *  is close enough for the system font stack to keep lines from overflowing. */
function fitText(s: string, fontSizeMm: number, maxWidthMm: number): string {
  const avgCharW = fontSizeMm * 0.55;
  const maxChars = Math.max(3, Math.floor(maxWidthMm / avgCharW));
  if (s.length <= maxChars) return s;
  return s.slice(0, Math.max(1, maxChars - 1)) + "…";
}

function renderWifiCardContent(data: WifiCardData, x: number, topY: number, w: number): string {
  const security = data.security === "nopass" ? "Open network" : data.security;
  const ssidLine = fitText(data.ssid || "—", 3.4, w);
  const passLine =
    data.security === "nopass" ? "—" : fitText(data.password || "(no password)", 3.0, w);
  return [
    text(x, topY + 4.8, "Wi-Fi", 4.4, { weight: 700 }),
    text(x, topY + 9, "Scan with your phone camera", 2.4, { fill: "#64748b" }),
    text(x, topY + 17, "NETWORK", 2.0, { fill: "#94a3b8", weight: 600, letterSpacing: 0.3 }),
    text(x, topY + 21.4, ssidLine, 3.4, { weight: 600 }),
    text(x, topY + 28, "PASSWORD", 2.0, { fill: "#94a3b8", weight: 600, letterSpacing: 0.3 }),
    text(x, topY + 32.4, passLine, 3.0, { mono: true }),
    text(x, topY + 40.5, `Security: ${security}`, 2.2, { fill: "#64748b" }),
    data.hidden ? text(x, topY + 44, "Hidden network", 2.2, { fill: "#64748b" }) : "",
  ].join("");
}

function renderVcardCardContent(data: VcardCardData, x: number, topY: number, w: number): string {
  const name = [data.firstName, data.lastName].filter(Boolean).join(" ");
  const subline = [data.title, data.organization].filter(Boolean).join(" · ");
  return [
    text(x, topY + 5.2, fitText(name || "—", 4.0, w), 4.0, { weight: 700 }),
    subline ? text(x, topY + 9.5, fitText(subline, 2.6, w), 2.6, { fill: "#64748b" }) : "",
    data.phone
      ? text(x, topY + 17, "PHONE", 2.0, { fill: "#94a3b8", weight: 600, letterSpacing: 0.3 })
      : "",
    data.phone ? text(x, topY + 20.8, fitText(data.phone, 2.8, w), 2.8, {}) : "",
    data.email
      ? text(x, topY + 26.5, "EMAIL", 2.0, { fill: "#94a3b8", weight: 600, letterSpacing: 0.3 })
      : "",
    data.email ? text(x, topY + 30.3, fitText(data.email, 2.6, w), 2.6, { mono: true }) : "",
    data.website
      ? text(x, topY + 36, "WEB", 2.0, { fill: "#94a3b8", weight: 600, letterSpacing: 0.3 })
      : "",
    data.website
      ? text(x, topY + 39.8, fitText(data.website.replace(/^https?:\/\//, ""), 2.6, w), 2.6, {
          mono: true,
          fill: "#475569",
        })
      : "",
  ].join("");
}

function text(
  x: number,
  y: number,
  content: string,
  fontSizeMm: number,
  opts: { fill?: string; weight?: number; letterSpacing?: number; mono?: boolean },
): string {
  if (!content) return "";
  const fill = opts.fill ?? "#0f172a";
  const family = opts.mono ? MONO_STACK : FONT_STACK;
  const weight = opts.weight ?? 400;
  const ls = opts.letterSpacing ? ` letter-spacing="${opts.letterSpacing}"` : "";
  return `<text x="${x.toFixed(3)}" y="${y.toFixed(3)}" font-family="${family}" font-size="${fontSizeMm}" font-weight="${weight}"${ls} fill="${fill}">${escapeXml(content)}</text>`;
}
